import type { ThemeOptions } from '@mui/material';

const defaultTheme: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1650, // all default except xl
    },
  },
  components: {
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#54A037',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#12284C',
          color: '#F3F3F3',
          borderRadius: '0',
        },
        deleteIcon: {
          color: '#F3F3F3',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: '#12284C',
          color: '#F3F3F3',
          borderRadius: '0',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#12284C',
          color: '#F3F3F3',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#3C496B',
            color: '#F3F3F3',
          },
          color: '#F3F3F3',
          borderColor: '#F3F3F3',
          '&.Mui-selected': {
            backgroundColor: '#3C496B',
            color: '#F3F3F3',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: '#F3F3F3',
          color: '#F3F3F3',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#12284C',
          color: '#F3F3F3',
          borderRadius: '0',
          filter: 'drop-shadow(0 0.2rem 0.3rem rgba(0, 0, 0, 0.5))',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#12284C',
          color: '#F3F3F3',
          borderRadius: '0',
          filter: 'drop-shadow(0 0.2rem 0.3rem rgba(0, 0, 0, 0.5))',
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          backgroundColor: '#12284C',
          color: '#F3F3F3',
          scrollbarWidth: 'thin',
          borderRadius: '0',
          paddingTop: '0',
          paddingBottom: '0',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: '0',
          backgroundColor: '#12284C',
          color: '#F3F3F3',
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          background: 'unset',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '0',
          '&.Mui-expanded': {
            boxShadow: 3,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderRadius: '0',
          '&:hover': {
            backgroundColor: '#3C496B',
            color: '#F3F3F3',
          },
          '&.Mui-expanded': {
            borderRadius: '0',
            boxShadow: 3,
            borderTop: '1px solid #54A037',
            '&:hover': {
              backgroundColor: '#3C496B',
              color: '#F3F3F3',
            },
          },
        },
        expandIconWrapper: {
          color: '#F3F3F3',
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          color: '#F3F3F3',
          borderRadius: '0',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: '#B5B5B5',
        },
        track: {
          opacity: 0.4,
          backgroundColor: '#E7888F',
          '.Mui-checked.Mui-checked + &': {
            opacity: 0.8,
            backgroundColor: '#6CC34A',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#F3F3F3',
          '&.Mui-checked': {
            color: '#6CC34A',
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
          padding: '0.5rem',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderColor: '#54A037',
          backgroundColor: '#F3F3F3',
          color: '#12284C',
          ':focus': {
            backgroundColor: '#F3F3F3',
            color: '#12284C',
          },
          ':hover': {
            backgroundColor: ' #3C496B',
            color: '#F3F3F3',
            borderTopColor: '#54A037',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#12284C',
          color: '#F3F3F3',
          borderRadius: '0',
          boxShadow: 'none',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: ({ ownerState }) => {
          if (ownerState.variant === 'button') {
            return {
              color: '#F3F3F3',
              textDecoration: 'underline',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              font: 'inherit',
              '&:hover': {
                textDecoration: 'underline',
                color: '#6F7A96',
              },
            };
          }
          return {};
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => {
          if (ownerState.variant === 'contained') {
            return {
              backgroundColor: '#F3F3F3',
              color: '#2A395C',
              '&:hover': {
                backgroundColor: '#3C496B',
                color: '#F3F3F3',
                '& a': {
                  color: '#F3F3F3',
                },
              },
              textDecoration: 'none',
              borderRadius: '5px',
              '& a': {
                color: '#2A395C',
                textDecoration: 'none',
              },
            };
          }
          if (ownerState.variant === 'outlined') {
            return {
              color: '#FFFFFF',
              borderColor: '#F3F3F3',
              textDecoration: 'none',
              borderRadius: '5px',
              '&:hover': {
                backgroundColor: '#3C496B',
                color: '#FFFFFF',
                borderColor: '#F3F3F3',
                textDecoration: 'none',
              },
              '& a': {
                color: '#F3F3F3',
                textDecoration: 'none',
              },
            };
          }
          return {};
        },
      },
    },
  },
  typography: {
    button: {
      textTransform: 'none',
    },
    h1: {
      fontWeight: 'bold',
    },
    h2: {
      fontWeight: 'bold',
    },
    h3: {
      fontWeight: 'bold',
    },
    h4: {
      fontWeight: 'bold',
    },
    h5: {
      fontWeight: 'bold',
      fontSize: '1.375rem',
    },
    h6: {
      fontWeight: 'bold',
    },
  },
};

export default defaultTheme;
